import { useForm } from 'react-hook-form'
import { imageSchema } from './validations'
import { zodResolver } from '@hookform/resolvers/zod'
import styled from 'styled-components'
import { Fragment, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getPublicUrl, getUploadImageUrl, IMAGE_TYPE } from '../../../helpers/url'
import Swal from 'sweetalert2'
import axios from 'axios'
import config from '../../../config'
import Cookies from 'js-cookie'
import { IMAGE_NAME_TYPE } from '../../../constants/configs'

const ImageUploadForm = ({ name, deviceType, onSave }) => {
  const dispatch = useDispatch()
  const { imageData } = useSelector((state) => state.webConfig)
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: zodResolver(imageSchema),
  })

  const imagePreview = watch('url')

  const onSubmit = async (data) => {
    await Swal.fire({
      title: 'อัพโหลดรูปภาพ',
      text: 'คุณต้องการอัพโหลดรูปภาพหรือไม่',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'บันทึก',
      cancelButtonText: 'ยกเลิก',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const res = await axios.patch(
            `${config.apiAdminGateway}/api/v1/admin/configs/image`,
            { ...data, deviceType, name: IMAGE_NAME_TYPE[name] },
            {
              headers: { Authorization: `Bearer ${Cookies.get('accessToken')}` },
            },
          )

          if (res.data.status.code !== 0) {
            throw new Error('Error updating round close detail')
          }

          Swal.fire({
            title: 'อัพโหลดรูปภาพสำเร็จ',
            icon: 'success',
          }).then(() => onSave && onSave('roundCloseDetail', data))
          return true
        } catch (error) {
          console.error(error)
          Swal.showValidationMessage('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง')
          return false
        }
      },
    })
  }

  const handleChangeImage = (e) => {
    if (e.target.files.length === 0) {
      setValue('url', '')
      return
    }

    const file = e.target.files[0]

    const image = new Image()
    image.src = URL.createObjectURL(file)

    image.onload = async () => {
      dispatch({ type: 'loading_show' })
      try {
        const imageType = file.type.includes('png') ? IMAGE_TYPE.PNG : IMAGE_TYPE.JPEG
        const url = await getUploadImageUrl(imageType)
        const imageUrl = getPublicUrl(url)

        const response = await fetch(url, {
          method: 'PUT',
          body: file,
        })

        if (!response.ok) throw new Error('Error uploading image')

        setValue('url', imageUrl)
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'ไม่สามารถอัพโหลดได้',
          showCloseButton: true,
        })

        setValue('url', '')
      } finally {
        dispatch({ type: 'loading_hide' })
      }
    }
  }

  const handleClickClearImage = () => {
    setValue('url', '')
  }

  const getImage = (image) => {
    if (!image) return

    if (typeof image === 'string') {
      return image
    } else if (image?.length > 0) {
      return URL.createObjectURL(image[0])
    }
  }

  useEffect(() => {
    if (imageData) {
      reset({
        url: imageData.filter((data) => data.deviceType === deviceType && data.imageName === name)[0]?.url ?? '',
      })
    }
  }, [deviceType, imageData, name, reset])

  return (
    <Form as="form" onSubmit={handleSubmit(onSubmit)}>
      <InputContainer>
        <label>รูป {deviceType}</label>
        <input
          type="file"
          id={`imageUpload-${deviceType}`}
          accept="image/jpeg, image/png"
          {...register('url', { onChange: handleChangeImage })}
          style={{ display: 'none' }}
        />
        <div style={{ position: 'relative' }}>
          {!imagePreview ? (
            <ImageUploadLabel htmlFor={`imageUpload-${deviceType}`}>อัปโหลดรูปภาพ</ImageUploadLabel>
          ) : (
            <Fragment>
              <img src={getImage(imagePreview)} alt="Preview" style={{ width: '100%', height: '100%' }} />
              <CloseButton type="button" onClick={handleClickClearImage}>
                X
              </CloseButton>
            </Fragment>
          )}
        </div>
        {errors.image && <ErrorMessageText>{errors.image.message}</ErrorMessageText>}
      </InputContainer>
      <SubmitButton>บันทึกรูปภาพ</SubmitButton>
    </Form>
  )
}

export default ImageUploadForm

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: red;
  color: white;
  border: none;
  padding: 0.5rem;
  border-radius: 50%;
  aspect-ratio: 1;
`

const ImageUploadLabel = styled.label`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem;
  background-color: #f2f2f2;
  border: 1px solid #e0e0e0;
  border-radius: 0.5rem;
  cursor: pointer;
  color: #333;
  font-size: 1rem;
  font-weight: 500;
  transition: 0.3s;
  &:hover {
    background-color: #e0e0e0;
  }
`

const ErrorMessageText = styled.span`
  color: red;
  font-size: 0.8rem;
`

const SubmitButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: 0.3s;
  &:hover {
    background-color: #0056b3;
  }
`
